import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getAttribution, track } from "@/lib/attribution";
import { SMS_CONSENT_TEXT } from "@/lib/consent";

const TYPE_MAP = {
  message: { context: "Contact message", source: "contact_form" },
  callback: { context: "Callback request", source: "callback_request" },
  consultation: { context: "Consultation request", source: "consultation_request" },
};

export default function Contact() {
  const { config } = useSite();
  const { search } = useLocation();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", message: "",
    requestType: new URLSearchParams(search).get("type") === "callback" ? "callback" : "message",
    consent: false, smsConsent: false,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    const type = TYPE_MAP[form.requestType] || TYPE_MAP.message;
    try {
      const res = await api.post("/leads", {
        serviceId: "contact",
        audience: "business",
        name: form.name,
        email: form.email,
        phone: form.phone,
        contactConsent: { granted: form.consent, policyVersion: config.policyVersion, recordedAt: new Date().toISOString() },
        smsConsent: { granted: form.smsConsent, text: SMS_CONSENT_TEXT, recordedAt: new Date().toISOString() },
        contextService: type.context,
        serviceAnswer: form.message,
        attribution: { ...getAttribution(), source: type.source, page: "/contact" },
      }).catch((err) => { throw new Error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed"); });
      setResult(res.data);
      track(type.source === "contact_form" ? "contact_submit" : `${type.source}_submit`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Seo
        title="Contact | Coastal Energy — Commercial Solar ROI"
        description="Talk to Coastal Energy's commercial solar team: call 310-881-5155, request a callback or request a consultation. Commercial Solar ROI is Coastal Energy's dedicated commercial solar platform."
      />
      <section className="page-hero">
        <div className="wrap">
          <span className="eyebrow">TALK IT THROUGH</span>
          <h1 data-testid="contact-heading">Get your quote.</h1>
          <p className="lead">
            We install commercial solar — every message becomes a saved project record and the fastest path to a quote. Call, or send your details below.
          </p>
          <p className="micro" style={{ marginTop: 12 }}>
            Commercial Solar ROI is part of Coastal Energy. Messages submitted here reach the commercial sales team, led by
            Lawrence Williams — Coastal Energy delivers the applicable project services.
          </p>
          <p className="micro" style={{ marginTop: 12 }}>
            Want proof first? <Link to="/projects" style={{ fontWeight: 800, color: "var(--teal)" }}>See the jobs we've done ↗</Link>
          </p>
        </div>
      </section>
      <section style={{ paddingBottom: 40 }}>
        <div className="wrap contact-grid">
          <Reveal>
            <div className="infopanel" data-testid="contact-panel">
              <span className="eyebrow">DIRECT LINE</span>
              <h2 style={{ margin: "12px 0 16px" }}>{config.phone}</h2>
              <p style={{ color: "#c1d6cf", fontSize: 14 }}>
                Ask about any service on the site, availability in your area, or a system you already own. If your question is about a saved project, keep the reference handy.
              </p>
              <p style={{ marginTop: 20, fontSize: 13 }}>
                <a href={config.phoneHref} data-testid="contact-phone-link">Call {config.phone} ↗</a>
              </p>
              <p style={{ marginTop: 30, fontSize: 11, color: "#9db8b1" }}>
                Prefer writing? Use the form and include your address and a recent bill total — it speeds up the first review.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="panel" data-testid="contact-form-panel">
              {result ? (
                <div data-testid="contact-success" role="status">
                  <span className="eyebrow">MESSAGE SAVED</span>
                  <h2 style={{ margin: "12px 0" }}>Reference {result.reference}</h2>
                  <p className="muted" style={{ fontSize: 13 }}>
                    Your {form.requestType === "callback" ? "callback request" : form.requestType === "consultation" ? "consultation request" : "message"} is saved and reaches the commercial sales team. The team follows up on saved records; for anything urgent, call {config.phone}.
                  </p>
                  <p className="muted" style={{ fontSize: 13 }}>
                    Next steps while you wait: <Link to="/#roi">explore your solar ROI</Link>, <Link to="/projects">browse completed projects</Link>, or <button type="button" className="textbutton" onClick={() => { setResult(null); }}>upload your commercial electric bill</button>.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form">
                  <div className="twofields">
                    <label className="field">
                      Full name
                      <input required maxLength={100} autoComplete="name" data-testid="contact-name" value={form.name} onChange={set("name")} />
                    </label>
                    <label className="field">
                      Email
                      <input required type="email" autoComplete="email" data-testid="contact-email" value={form.email} onChange={set("email")} />
                    </label>
                  </div>
                  <label className="field">
                    Phone
                    <input required type="tel" autoComplete="tel" data-testid="contact-phone" value={form.phone} onChange={set("phone")} />
                  </label>
                  <label className="field">
                    I'd like to…
                    <select data-testid="contact-request-type" value={form.requestType} onChange={set("requestType")}>
                      <option value="message">Send a message</option>
                      <option value="callback">Request a callback</option>
                      <option value="consultation">Request a consultation</option>
                    </select>
                  </label>
                  <label className="field">
                    How can we help?
                    <textarea rows={5} maxLength={1200} data-testid="contact-message" value={form.message} onChange={set("message")} />
                  </label>
                  <input className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" name="company_website" value="" readOnly />
                  <label className="check">
                    <input type="checkbox" required data-testid="contact-consent" checked={form.consent} onChange={set("consent")} />
                    <span>
                      I agree to be contacted about my request and have reviewed the <a href="/privacy" target="_blank" rel="noreferrer">privacy information</a>.
                    </span>
                  </label>
                  <label className="check" data-testid="contact-sms-consent-row">
                    <input type="checkbox" data-testid="contact-sms-consent" checked={form.smsConsent} onChange={set("smsConsent")} />
                    <span>{SMS_CONSENT_TEXT}</span>
                  </label>
                  <button className="btn" type="submit" disabled={busy} data-testid="contact-submit" style={{ marginTop: 18 }}>
                    {busy ? "Sending securely…" : "Send my message"} <span>↗</span>
                  </button>
                  {error && (
                    <p className="status" role="alert" style={{ color: "#a33a17" }}>
                      {error} Or call <a href={config.phoneHref}>{config.phone}</a>.
                    </p>
                  )}
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
